if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.initialized = false;
        this.initializationFrame = null;
      }

      connectedCallback() {
        this.initialize();
      }

      initialize() {
        if (this.initialized) return;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        const variantMediaMap = this.querySelector(
          "[data-st-variant-media-map]",
        );

        if (!this.elements.viewer || !variantMediaMap) {
          if (!this.initializationFrame) {
            this.initializationFrame = window.requestAnimationFrame(() => {
              this.initializationFrame = null;
              this.initialize();
            });
          }
          return;
        }

        this.initialized = true;
        this.mql = window.matchMedia("(min-width: 750px)");
        this.sectionId = this.id.replace("MediaGallery-", "");
        this.refreshVariantMediaMap();
        this.filterVariantMedia();
        this.elements.viewer?.slider?.addEventListener("scroll", () =>
          this.scheduleVisibleMediaCounterUpdate(),
        );
        window.addEventListener("resize", () =>
          this.scheduleVisibleMediaCounterUpdate(),
        );

        if (
          typeof subscribe === "function" &&
          typeof PUB_SUB_EVENTS !== "undefined"
        ) {
          subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId !== this.sectionId) return;
            const updatedGallery = event.data.html?.getElementById?.(this.id);
            this.refreshVariantMediaMap(updatedGallery || this);
            this.filterVariantMedia(event.data.variant);
          });
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          "slideChanged",
          debounce(this.onSlideChanged.bind(this), 500),
        );
        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector("button")
              .addEventListener(
                "click",
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false,
                ),
              );
          });
        if (
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        )
          this.removeListSemantic();
      }

      getVariantMediaMap(source = this) {
        const script = source.querySelector?.("[data-st-variant-media-map]");
        if (!script) {
          return {
            colourOptionIndex: -1,
            optionNames: [],
            selectedVariantId: null,
            variants: [],
          };
        }

        try {
          const data = JSON.parse(script.textContent);
          return {
            colourOptionIndex: Number.isInteger(data.colour_option_index)
              ? data.colour_option_index
              : -1,
            optionNames: Array.isArray(data.option_names)
              ? data.option_names
              : [],
            selectedVariantId: data.selected_variant_id,
            variants: Array.isArray(data.variants) ? data.variants : [],
          };
        } catch (error) {
          return {
            colourOptionIndex: -1,
            optionNames: [],
            selectedVariantId: null,
            variants: [],
          };
        }
      }

      refreshVariantMediaMap(source = this) {
        this.variantMediaData = this.getVariantMediaMap(source);
        if (this.variantMediaData.colourOptionIndex < 0) {
          this.variantMediaData.colourOptionIndex =
            this.inferColourOptionIndex();
        }
        this.variantMediaMap = this.variantMediaData.variants;
      }

      inferColourOptionIndex() {
        const mediaSlugs = Array.from(
          this.querySelectorAll("[data-st-media-text]"),
        ).map((item) => this.getColourSlug(item.dataset.stMediaText));
        const optionCount = Math.max(
          0,
          ...this.variantMediaData.variants.map(
            (variant) => variant.options?.length || 0,
          ),
        );

        for (let optionIndex = 0; optionIndex < optionCount; optionIndex += 1) {
          const optionName = String(
            this.variantMediaData.optionNames?.[optionIndex] || "",
          )
            .toLowerCase()
            .trim();
          const optionSlugs = [
            ...new Set(
              this.variantMediaData.variants
                .map((variant) =>
                  this.getColourSlug(variant.options?.[optionIndex]),
                )
                .filter(Boolean),
              ),
          ];

          if (
            optionName === "size" ||
            optionName === "sizes" ||
            this.isSizeOnlyOption(optionSlugs)
          ) {
            continue;
          }

          const matchingOptionCount = optionSlugs.filter((optionSlug) =>
            mediaSlugs.some((mediaSlug) => mediaSlug.includes(optionSlug)),
          ).length;

          if (matchingOptionCount >= 2) return optionIndex;
        }

        return -1;
      }

      isSizeOnlyOption(optionSlugs) {
        const sizeSlugs = new Set([
          "xxs",
          "xs",
          "s",
          "m",
          "l",
          "xl",
          "xxl",
          "xxxl",
          "2xl",
          "3xl",
          "4xl",
          "5xl",
          "6xl",
          "small",
          "medium",
          "large",
          "x-small",
          "x-large",
          "os",
          "one-size",
        ]);

        return (
          optionSlugs.length > 0 &&
          optionSlugs.every((optionSlug) => sizeSlugs.has(optionSlug))
        );
      }

      getColourSlug(value) {
        return String(value || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "");
      }

      getActiveColourData(activeVariant) {
        const colourOptionIndex = this.variantMediaData.colourOptionIndex;
        if (
          colourOptionIndex < 0 ||
          !activeVariant?.options?.[colourOptionIndex]
        ) {
          return { selectedSlug: "", otherSlugs: [] };
        }

        const selectedColour = activeVariant.options[colourOptionIndex];
        const selectedSlug = this.getColourSlug(selectedColour);
        const otherSlugs = [
          ...new Set(
            this.variantMediaMap
              .filter(
                (variant) =>
                  variant.options?.[colourOptionIndex] !== selectedColour,
              )
              .flatMap((variant) =>
                this.getColourSlug(variant.options?.[colourOptionIndex]),
              ),
          ),
        ].filter(Boolean);

        return { selectedSlug, otherSlugs };
      }

      getActiveVariant(variant) {
        if (variant?.id)
          return this.variantMediaMap.find(
            (item) => String(item.id) === String(variant.id),
          );
        if (this.variantMediaData.selectedVariantId) {
          return this.variantMediaMap.find(
            (item) =>
              String(item.id) ===
              String(this.variantMediaData.selectedVariantId),
          );
        }
        const activeMediaId = this.elements.viewer
          ?.querySelector("[data-media-id].is-active")
          ?.dataset.mediaId?.split("-")
          .pop();
        if (!activeMediaId) return null;
        return this.variantMediaMap.find(
          (item) => String(item.featured_media_id) === String(activeMediaId),
        );
      }

      filterVariantMedia(variant) {
        if (!this.elements?.viewer) return;

        const mediaGroupStarts = [
          ...new Set(
            this.variantMediaMap
              .map((item) => item.featured_media_position)
              .filter(Boolean),
          ),
        ].sort((a, b) => a - b);
        const activeVariant = this.getActiveVariant(variant);
        const colourData = this.getActiveColourData(activeVariant);

        if (mediaGroupStarts.length < 2 && !colourData.selectedSlug) {
          this.querySelectorAll("[data-st-media-position]").forEach((item) =>
            item.classList.remove("st-variant-media-hidden"),
          );
          this.updateThumbnailNavigation();
          this.elements.viewer?.slider?.resetPages?.();
          this.elements.thumbnails?.slider?.resetPages?.();
          this.scheduleVisibleMediaCounterUpdate();
          return;
        }

        if (
          !activeVariant?.featured_media_position &&
          !colourData.selectedSlug
        ) {
          const firstVariantWithMedia = this.variantMediaMap.find(
            (item) => item.featured_media_position,
          );
          if (!firstVariantWithMedia) {
            this.showAllVariantMedia();
            return;
          }
          this.filterVariantMedia({ id: firstVariantWithMedia.id });
          return;
        }

        const startPosition = activeVariant.featured_media_position;
        const nextPosition = mediaGroupStarts.find(
          (position) => position > startPosition,
        );
        const firstStartPosition = mediaGroupStarts[0];
        const selectedStartIndex = mediaGroupStarts.indexOf(startPosition);
        const startsAreConsecutive = mediaGroupStarts.every(
          (position, index) => position === firstStartPosition + index,
        );
        const mediaItems = this.querySelectorAll("[data-st-media-position]");

        mediaItems.forEach((item) => {
          const position = Number(item.dataset.stMediaPosition);
          const mediaText = item.dataset.stMediaText || "";
          const mediaSlug = this.getColourSlug(mediaText);
          const hasSelectedColour = Boolean(
            colourData.selectedSlug &&
            mediaSlug.includes(colourData.selectedSlug),
          );
          const hasOtherColour = colourData.otherSlugs.some((slug) =>
            mediaSlug.includes(slug),
          );
          const shouldHideByColour = hasOtherColour && !hasSelectedColour;
          const shouldUsePositionFallback =
            !colourData.selectedSlug || (!hasSelectedColour && !hasOtherColour);
          const shouldHideByInterleavedPosition =
            startsAreConsecutive &&
            selectedStartIndex >= 0 &&
            position >= firstStartPosition &&
            (position - firstStartPosition) % mediaGroupStarts.length !==
              selectedStartIndex;
          const shouldHideByGroupedPosition =
            position < startPosition ||
            (nextPosition && position >= nextPosition);
          const shouldHideByPosition =
            shouldUsePositionFallback &&
            (startsAreConsecutive
              ? shouldHideByInterleavedPosition
              : shouldHideByGroupedPosition);
          const shouldHide = shouldHideByColour || shouldHideByPosition;
          item.classList.toggle("st-variant-media-hidden", shouldHide);
        });

        const activeMedia = this.elements.viewer.querySelector(
          "[data-media-id].is-active",
        );
        const firstVisibleMedia =
          this.getFirstVisibleMedia() ||
          this.restoreVariantFallback(activeVariant);

        if (
          firstVisibleMedia &&
          (!activeMedia ||
            activeMedia.classList.contains("st-variant-media-hidden"))
        ) {
          this.setActiveMedia(firstVisibleMedia.dataset.mediaId, false);
        }

        this.updateThumbnailNavigation();
        this.elements.viewer?.slider?.resetPages?.();
        this.elements.thumbnails?.slider?.resetPages?.();
        this.scheduleVisibleMediaCounterUpdate();
      }

      showAllVariantMedia() {
        this.querySelectorAll("[data-st-media-position]").forEach((item) =>
          item.classList.remove("st-variant-media-hidden"),
        );
        this.updateThumbnailNavigation();
        this.elements.viewer?.slider?.resetPages?.();
        this.elements.thumbnails?.slider?.resetPages?.();
        this.scheduleVisibleMediaCounterUpdate();
      }

      updateThumbnailNavigation() {
        if (!this.elements.thumbnails) return;

        const visibleThumbnailCount = this.elements.thumbnails.querySelectorAll(
          "[data-target]:not(.st-variant-media-hidden)",
        ).length;
        this.elements.thumbnails
          .querySelectorAll('button[name="previous"], button[name="next"]')
          .forEach((button) =>
            button.classList.toggle("hidden", visibleThumbnailCount <= 2),
          );
      }

      getFirstVisibleMedia() {
        return this.elements.viewer?.querySelector(
          "[data-media-id]:not(.st-variant-media-hidden)",
        );
      }

      restoreVariantFallback(activeVariant) {
        const featuredMediaId = activeVariant?.featured_media_id;
        const featuredMedia = featuredMediaId
          ? Array.from(
              this.elements.viewer.querySelectorAll("[data-media-id]"),
            ).find(
              (item) =>
                item.dataset.mediaId?.split("-").pop() ===
                String(featuredMediaId),
            )
          : null;
        const fallbackMedia =
          featuredMedia || this.elements.viewer.querySelector("[data-media-id]");

        if (!fallbackMedia) return null;

        const fallbackPosition = fallbackMedia.dataset.stMediaPosition;
        this.querySelectorAll("[data-st-media-position]").forEach((item) => {
          if (item.dataset.stMediaPosition === fallbackPosition) {
            item.classList.remove("st-variant-media-hidden");
          }
        });

        fallbackMedia.classList.remove("st-variant-media-hidden");
        return fallbackMedia;
      }

      scheduleVisibleMediaCounterUpdate() {
        window.requestAnimationFrame(() => this.updateVisibleMediaCounter());
        window.setTimeout(() => this.updateVisibleMediaCounter(), 80);
        window.setTimeout(() => this.updateVisibleMediaCounter(), 250);
      }

      updateVisibleMediaCounter() {
        const visibleMedia = Array.from(
          this.elements.viewer?.querySelectorAll(
            "[data-media-id]:not(.st-variant-media-hidden)",
          ) || [],
        );
        if (!visibleMedia.length) return;

        const slider = this.elements.viewer?.slider;
        const scrollLeft = slider?.scrollLeft || 0;
        let currentIndex = 0;

        if (slider) {
          currentIndex = visibleMedia.reduce((closestIndex, item, index) => {
            const closestDistance = Math.abs(
              visibleMedia[closestIndex].offsetLeft - scrollLeft,
            );
            const itemDistance = Math.abs(item.offsetLeft - scrollLeft);
            return itemDistance < closestDistance ? index : closestIndex;
          }, 0);
        } else {
          const currentMedia = this.elements.viewer?.querySelector(
            "[data-media-id].is-active:not(.st-variant-media-hidden)",
          );
          currentIndex = Math.max(visibleMedia.indexOf(currentMedia), 0);
        }

        const counter = this.elements.viewer?.querySelector(".slider-counter");
        const currentCounter = counter?.querySelector(
          ".slider-counter--current",
        );
        const totalCounter = counter?.querySelector(".slider-counter--total");
        const displayIndex = currentIndex + 1;

        if (this.elements.viewer) {
          this.elements.viewer.currentPage = displayIndex;
          this.elements.viewer.totalPages = visibleMedia.length;
        }

        if (currentCounter) currentCounter.textContent = displayIndex;
        if (totalCounter) totalCounter.textContent = visibleMedia.length;
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
        );
        this.setActiveThumbnail(thumbnail);
        this.scheduleVisibleMediaCounterUpdate();
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector("[data-media-id]");
        if (!activeMedia) {
          return;
        }
        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia?.classList?.add("is-active");

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`,
            );
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: "smooth" });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) {
          this.scheduleVisibleMediaCounterUpdate();
          return;
        }
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition,
          );
        }
        this.scheduleVisibleMediaCounterUpdate();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img",
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position,
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation"),
        );
      }
    },
  );
}
